import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  ReleaseSchedule,
  ReleaseScheduleCreateRequest,
  ReleaseScheduleUpdateRequest,
} from '@/lib/api-admin/types';
import {
  ApiClientError,
  idempotencyKeyFor,
  TransportError,
  type ApiError as ApiErrorPayload,
  type ApiMeta,
} from '@/lib/http-core';
import { apiClient } from '@/lib/apiClient';

interface ErrorEnvelopeShape {
  success: false;
  error: ApiErrorPayload;
  meta?: ApiMeta;
}

const throwIfError = (raw: unknown, status: number, op: string): never => {
  const env = raw as ErrorEnvelopeShape | null | undefined;
  if (env && env.success === false && env.error) {
    throw new ApiClientError(env.error, {
      status,
      ...(env.meta ? { meta: env.meta } : {}),
    });
  }
  throw new TransportError(`Release schedule ${op} failed with status ${status}`, raw);
};

const invalidate = (qc: ReturnType<typeof useQueryClient>) =>
  qc.invalidateQueries({ queryKey: ['admin', 'release-schedules'] });

export const useCreateReleaseScheduleMutation = () => {
  const qc = useQueryClient();
  return useMutation<ReleaseSchedule, Error, ReleaseScheduleCreateRequest>({
    mutationFn: async (body) => {
      const path = '/admin/v1/release-schedules' as const;
      const idempotencyKey = idempotencyKeyFor('POST', path, body);
      const { data, error, response } = await apiClient.raw.POST(path, {
        body,
        params: { header: { 'Idempotency-Key': idempotencyKey } },
      });
      if (!data || data.success !== true)
        throwIfError(error ?? data, response.status, 'create');
      return (data as { data: ReleaseSchedule }).data;
    },
    onSuccess: () => invalidate(qc),
  });
};

export interface UpdateReleaseInput {
  scheduleId: string;
  body: ReleaseScheduleUpdateRequest;
}

export const useUpdateReleaseScheduleMutation = () => {
  const qc = useQueryClient();
  return useMutation<ReleaseSchedule, Error, UpdateReleaseInput>({
    mutationFn: async ({ scheduleId, body }) => {
      const path = '/admin/v1/release-schedules/{scheduleId}' as const;
      const concrete = path.replace('{scheduleId}', scheduleId);
      const idempotencyKey = idempotencyKeyFor('PATCH', concrete, body);
      const { data, error, response } = await apiClient.raw.PATCH(path, {
        body,
        params: {
          path: { scheduleId },
          header: { 'Idempotency-Key': idempotencyKey },
        },
      });
      if (!data || data.success !== true)
        throwIfError(error ?? data, response.status, 'update');
      return (data as { data: ReleaseSchedule }).data;
    },
    onSuccess: () => invalidate(qc),
  });
};

export interface CancelReleaseInput {
  scheduleId: string;
  cancelReason: string;
}

export const useCancelReleaseScheduleMutation = () => {
  const qc = useQueryClient();
  return useMutation<ReleaseSchedule, Error, CancelReleaseInput>({
    mutationFn: async ({ scheduleId, cancelReason }) => {
      const path = '/admin/v1/release-schedules/{scheduleId}' as const;
      const concrete = path.replace('{scheduleId}', scheduleId);
      const idempotencyKey = idempotencyKeyFor('DELETE', concrete, { cancelReason });
      const { data, error, response } = await apiClient.raw.DELETE(path, {
        params: {
          path: { scheduleId },
          query: { cancelReason },
          header: { 'Idempotency-Key': idempotencyKey },
        },
      });
      if (!data || data.success !== true)
        throwIfError(error ?? data, response.status, 'cancel');
      return (data as { data: ReleaseSchedule }).data;
    },
    onSuccess: () => invalidate(qc),
  });
};
